/**
 * vizRunner.js - Client-side visualization renderer
 *
 * Executes user-provided visualization code via a sandboxed Web Worker
 * and renders the resulting Plotly spec using Plotly.js loaded from CDN.
 *
 * Public API (backward-compatible):
 *   showLoading(container)
 *   renderVisualization(container, response)   ← used by VisualizationsTab + DashboardsTab
 *   renderDashboardPanels(panels)
 *
 * Additional exports:
 *   loadPlotly()                               ← returns Promise<Plotly>
 *   renderFigure(container, figure)            ← renders a pre-validated figure object
 */

import { executeVizCode } from './vizExecutor.js';

const PLOTLY_CDN = 'https://cdn.plot.ly/plotly-2.35.2.min.js';

let plotlyPromise = null;

export function loadPlotly() {
  if (plotlyPromise) return plotlyPromise;
  if (window.Plotly) {
    plotlyPromise = Promise.resolve(window.Plotly);
    return plotlyPromise;
  }
  plotlyPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PLOTLY_CDN;
    script.onload = () => resolve(window.Plotly);
    script.onerror = () => reject(new Error('Failed to load Plotly.js'));
    document.head.appendChild(script);
  });
  return plotlyPromise;
}

export function showLoading(container) {
  if (!container) return;
  container.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:200px;color:#6b7280;font-size:14px;">
      <span>Rendering visualization...</span>
    </div>
  `;
}

function showError(container, message) {
  if (!container) return;
  container.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:200px;color:#ef4444;font-size:14px;padding:16px;text-align:center;">
      <span>Error: ${message}</span>
    </div>
  `;
}

function showSchemaStalWarning(container) {
  if (!container) return;
  container.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:200px;gap:8px;padding:16px;text-align:center;">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span style="color:#92400e;font-size:13px;font-weight:500;">Schema out of sync</span>
      <span style="color:#b45309;font-size:12px;">A field referenced by this panel was added or removed.<br>Run a sync to update the table schema.</span>
    </div>
  `;
}

/**
 * Render a pre-built Plotly figure into a container.
 * Handles Plotly load + newPlot, catches render errors.
 *
 * @param {HTMLElement} container
 * @param {object} figure  - { data: [...], layout: {...} }
 * @returns {Promise<{error: object|null}>} - plotly error if thrown
 */
export async function renderFigure(container, figure) {
  if (!container) return { error: null };
  try {
    const Plotly = await loadPlotly();
    await Plotly.newPlot(container, figure.data, figure.layout || {}, {
      responsive: true,
      displayModeBar: true,
    });
    return { error: null };
  } catch (err) {
    return {
      error: {
        phase: 'plotly',
        message: err.message || 'Plotly render failed',
        line: null,
      },
    };
  }
}

/**
 * Execute user visualization code and render with Plotly.
 * Used by DashboardsTab and the quick-viewer in VisualizationsTab.
 *
 * @param {HTMLElement} container - DOM element to render into
 * @param {object} response - API response: { code, data, params }
 */
export async function renderVisualization(container, response) {
  if (!container) return;

  const { code, data, params } = response;

  if (!code) {
    showError(container, 'No visualization code provided');
    return;
  }

  try {
    const { figure, error } = await executeVizCode(code, data, params);

    if (error) {
      showError(container, error.message || 'Visualization error');
      return;
    }

    const { error: plotlyError } = await renderFigure(container, figure);
    if (plotlyError) {
      showError(container, plotlyError.message);
    }
  } catch (err) {
    showError(container, err.message || 'Failed to render visualization');
  }
}

/**
 * Render multiple dashboard panels.
 *
 * @param {Array<{element: HTMLElement, panelResult: object}>} panels
 */
export async function renderDashboardPanels(panels) {
  if (!panels || panels.length === 0) return;

  for (const { element, panelResult } of panels) {
    if (!element || !panelResult) continue;

    if (!panelResult.success) {
      if (panelResult.error_code === 'schema_stale') {
        showSchemaStalWarning(element);
      } else {
        showError(element, panelResult.error || 'Panel render failed');
      }
      continue;
    }

    await renderVisualization(element, panelResult);
  }
}
